import React, { useEffect, useMemo, useRef, useState } from "react";

const AUCTION_DURATION_SECONDS = 5 * 60;

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const parseTime = (value) => {
  if (!value) return null;
  const normalized = typeof value === "string" ? value.replace(" ", "T") : value;
  const ts = new Date(normalized).getTime();
  return Number.isFinite(ts) ? ts : null;
};

const formatClock = (seconds) => {
  const safe = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;
  return [hours, minutes, secs].map((n) => String(n).padStart(2, "0")).join(":");
};

const formatDateTime = (value) => {
  const ts = parseTime(value);
  if (!ts) return "";
  return new Intl.DateTimeFormat("he-IL", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(ts));
};

const AuctionCountdown = ({ auctionStatus, onUpdate, variant = "default" }) => {
  const [nowMs, setNowMs] = useState(Date.now());
  const updateRequestedRef = useRef(false);

  const serverNowValue = auctionStatus?.server_now_iso || auctionStatus?.server_now || auctionStatus?.server_time;
  const remainingSecondsFromServer = toFiniteNumber(
    auctionStatus?.remaining_seconds ?? auctionStatus?.seconds_until_end
  );
  const secondsUntilStartFromServer = toFiniteNumber(auctionStatus?.seconds_until_start);

  const serverOffsetMs = useMemo(() => {
    const serverNow = parseTime(serverNowValue);
    return serverNow ? Date.now() - serverNow : 0;
  }, [serverNowValue]);

  const startValue =
    auctionStatus?.auction_start_datetime_iso ||
    auctionStatus?.auction_start_at_iso ||
    auctionStatus?.auction_start_datetime ||
    auctionStatus?.auction_start_at;
  const endValue =
    auctionStatus?.auction_active_until_iso ||
    auctionStatus?.auction_end_time_iso ||
    auctionStatus?.auction_active_until ||
    auctionStatus?.auction_end_time ||
    auctionStatus?.auction_end_at;
  const startMs = parseTime(startValue);
  const serverNowMs = parseTime(serverNowValue);
  const responseStartMs =
    serverNowMs && secondsUntilStartFromServer !== null
      ? serverNowMs + secondsUntilStartFromServer * 1000
      : null;
  const responseEndMs =
    serverNowMs && remainingSecondsFromServer !== null
      ? serverNowMs + remainingSecondsFromServer * 1000
      : null;
  const endMs = responseEndMs || parseTime(endValue) || (startMs ? startMs + AUCTION_DURATION_SECONDS * 1000 : null);
  const effectiveNowMs = nowMs - serverOffsetMs;

  const effectiveStartMs = responseStartMs || startMs;
  const status = String(auctionStatus?.status ?? auctionStatus?.auction_status ?? "").toLowerCase();
  const statusIsActive = ["active", "live"].includes(status);
  const statusIsUpcoming = ["upcoming", "before_start", "scheduled", "future"].includes(status);
  const explicitHasStarted = auctionStatus?.has_started === true;
  const isUpcoming =
    !statusIsActive &&
    !explicitHasStarted &&
    (statusIsUpcoming || (!!effectiveStartMs && effectiveNowMs < effectiveStartMs));
  const hasEnded =
    !isUpcoming &&
    (status === "ended" || status === "finalized" || auctionStatus?.has_ended || (!!endMs && effectiveNowMs >= endMs));
  const isActive =
    !isUpcoming &&
    !hasEnded &&
    (statusIsActive || explicitHasStarted || (!!effectiveStartMs && !!endMs && effectiveNowMs >= effectiveStartMs && effectiveNowMs < endMs));
  const targetMs = isUpcoming ? effectiveStartMs : isActive ? endMs : null;
  const secondsLeft = targetMs ? Math.max(0, Math.ceil((targetMs - effectiveNowMs) / 1000)) : 0;

  useEffect(() => {
    if (!auctionStatus?.is_auction) return undefined;

    setNowMs(Date.now());
    updateRequestedRef.current = false;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [auctionStatus?.is_auction, serverNowValue, startValue, endValue, remainingSecondsFromServer, secondsUntilStartFromServer]);

  useEffect(() => {
    if (!auctionStatus?.is_auction || !targetMs || secondsLeft > 0 || updateRequestedRef.current) return;
    updateRequestedRef.current = true;
    onUpdate?.();
  }, [auctionStatus?.is_auction, targetMs, secondsLeft, onUpdate]);

  if (!auctionStatus?.is_auction) return null;

  const title = isUpcoming ? "המכרז טרם התחיל" : isActive ? "מועד סיום המכרז" : "המכרז הסתיים";
  const subtitle = isUpcoming
    ? "ניתן יהיה להגיש הצעות כשהמכרז יתחיל"
    : isActive
    ? "חלון ההגשה הוא 5 דקות מהפעילות האחרונה במכרז"
    : "לא ניתן להגיש הצעות נוספות";
  const detail = isUpcoming && startValue ? `תחילת המכרז: ${formatDateTime(startValue)}` : "";

  // Calculate time units
  const days = Math.floor(secondsLeft / 86400);
  const hours = Math.floor((secondsLeft % 86400) / 3600);
  const minutes = Math.floor((secondsLeft % 3600) / 60);
  const seconds = secondsLeft % 60;

  if (variant === "topFloating") {
    const compactLabel = hasEnded
      ? "המכרז הסתיים"
      : isUpcoming
        ? "המכרז מתחיל בעוד"
        : "מכרז פעיל";
    const compactUnits = days > 0
      ? [
          { value: days, label: "ימים" },
          { value: hours, label: "שעות" },
          { value: minutes, label: "דקות" },
          { value: seconds, label: "שניות" },
        ]
      : [
          { value: hours, label: "שעות" },
          { value: minutes, label: "דקות" },
          { value: seconds, label: "שניות" },
        ];

    return (
      <section
        className={`auction-countdown-top ${isActive ? "is-active" : ""} ${hasEnded ? "is-ended" : ""}`}
        aria-live="polite"
        dir="rtl"
      >
        <div className="auction-countdown-top__headline">
          <span className="auction-countdown-top__status" />
          <span className="auction-countdown-top__label">
            {compactLabel}
          </span>
        </div>
        {!hasEnded && (
          <div className="auction-countdown-top__time" dir="ltr">
            {compactUnits.map((unit) => (
              <span className="auction-countdown-top__unit" key={unit.label}>
                <strong>{String(unit.value).padStart(2, "0")}</strong>
                <small>{unit.label}</small>
              </span>
            ))}
          </div>
        )}
      </section>
    );
  }

  // Premium professional dashboard style with black boxes and glowing red numbers
  const TimeUnit = ({ value, label }) => (
    <div className="flex flex-col items-center justify-center rounded-lg bg-black border border-gray-800 shadow-lg min-w-[80px] px-5 py-4 sm:min-w-[70px] sm:px-4 sm:py-3">
      <div className="font-semibold text-4xl leading-none tabular-nums sm:text-3xl" style={{ color: '#ff0000'}}>
        {String(value).padStart(2, "0")}
      </div>
      <div className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
        {label}
      </div>
    </div>
  );

  // Professional status badge
  const StatusBadge = () => {
    if (hasEnded) {
      return (
        <div className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700">
          <span className="h-1.5 w-1.5 rounded-full bg-gray-500" />
          Closed
        </div>
      );
    }
    if (isActive) {
      return (
        <div className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold" style={{ backgroundColor: 'rgba(255, 140, 66, 0.1)', color: 'rgb(255, 140, 66)' }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: 'rgb(255, 140, 66)' }} />
          Active Auction
        </div>
      );
    }
    if (isUpcoming) {
      return (
        <div className="inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
          Upcoming
        </div>
      );
    }
    return null;
  };

  return (
    <section className="w-full px-3" aria-live="polite">
      <div className="mx-auto w-full max-w-[620px] rounded-xl bg-white border border-gray-200 shadow-md px-8 py-6 sm:max-w-[92vw] sm:px-5 sm:py-5">
 

        {/* Title in RTL */}
        <div className="mb-5 text-center text-lg font-bold text-gray-900 sm:mb-4 sm:text-base" dir="rtl">
          {title}
        </div>

        {/* Timer in LTR with English labels */}
        {(isActive || isUpcoming) && (
          <div className="mb-5 flex justify-center gap-3 sm:gap-2.5 sm:mb-4" dir="ltr">
            <TimeUnit value={days} label="ימים" />
            <TimeUnit value={hours} label="שעות" />
            <TimeUnit value={minutes} label="דקות" />
            <TimeUnit value={seconds} label="שניות" />
          </div>
        )}

  

      </div>
    </section>
  );
};

export default AuctionCountdown;
